import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useTelegram } from '../../context/TelegramContext';
import { useCurrency } from '../../context/CurrencyContext';
import SettingsSection from './SettingsSection';

const SelectContainer = styled.div`
  width: 100%;
  margin-top: 8px;
`;

const StyledSelect = styled.select`
  width: 100%;
  padding: 10px;
  border: none;
  border-radius: 10px;
  background: #f0f3f9;
  color: #333;
  box-shadow: inset 2px 2px 5px rgba(0, 0, 0, 0.05),
              inset -2px -2px 5px rgba(255, 255, 255, 0.5);
  appearance: none;
  cursor: pointer;
  
  &:focus {
    outline: none;
  }
`;

const SelectWrapper = styled.div`
  position: relative;
  width: 100%;
  
  &:after {
    content: '';
    position: absolute;
    top: 50%;
    right: 12px;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 5px solid #4a6fa1;
    pointer-events: none;
  }
`;

const FormMessage = styled.p`
  margin: 8px 0 0;
  font-size: 14px;
  color: ${props => props.isError ? '#e53e3e' : '#38a169'};
  transition: opacity 0.3s ease;
  opacity: ${props => props.visible ? 1 : 0};
`;

const CurrencySection = () => {
  const { t } = useTranslation();
  const { telegramUserId } = useTelegram();
  const currencyContext = useCurrency();
  // Проверяем доступные методы в контексте
  const updateCurrency = currencyContext.setPreferredCurrency || 
                          currencyContext.updatePreferredCurrency || 
                          currencyContext.setCurrency || 
                          (() => console.error("No currency update function found in context"));
  const [selectedCurrency, setSelectedCurrency] = useState('');
  const [message, setMessage] = useState({ text: '', isError: false, visible: false });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (currencyContext.preferredCurrency || currencyContext.currency) {
      setSelectedCurrency(currencyContext.preferredCurrency || currencyContext.currency);
      setIsLoading(false);
    } else {
      fetchUserCurrency();
    }
  }, [currencyContext, telegramUserId]);

  const fetchUserCurrency = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`https://fftg.farodinprojects.org:3000/get_preferred_currency?user_id=${telegramUserId}`);
      const data = await response.json();
      
      if (response.ok && data.currency) {
        setSelectedCurrency(data.currency);
        // Безопасно пытаемся обновить контекст
        try {
          updateCurrency(data.currency);
        } catch (err) {
          console.log('Could not update currency context:', err);
        }
      }
    } catch (error) {
      console.error('Error fetching currency preference:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCurrencyChange = async (e) => {
    const newCurrency = e.target.value;
    setSelectedCurrency(newCurrency);
    
    try {
      const response = await fetch('https://fftg.farodinprojects.org:3000/set_preferred_currency', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: telegramUserId,
          currency: newCurrency
        })
      });

      // Прочитаем тело ответа для анализа
      const data = await response.json().catch(() => ({}));
      
      // Проверим успешность по статусу и содержимому ответа
      if (response.ok && !data.error) {
        // Безопасно обновляем контекст валюты
        try {
          updateCurrency(newCurrency);
        } catch (err) {
          console.log('Could not update currency context:', err);
        }
        
        setMessage({ 
          text: t('currency_saved'), 
          isError: false,
          visible: true 
        });
      } else {
        // Формируем понятное сообщение об ошибке
        const errorMessage = data.error || t('currency_error');
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Currency update error:', error);
      
      setMessage({ 
        text: error.message || t('currency_error'), 
        isError: true,
        visible: true  
      });
    } finally {
      // В любом случае показываем сообщение на 3 секунды
      setTimeout(() => {
        setMessage(prev => ({ ...prev, visible: false }));
      }, 3000);
    }
  };

  const currencies = [
    { code: 'UAH', symbol: '₴' },
    { code: 'USD', symbol: '$' },
    { code: 'EUR', symbol: '€' },
    { code: 'GBP', symbol: '£' },
    { code: 'JPY', symbol: '¥' },
    { code: 'CNY', symbol: '¥' },
    { code: 'RUB', symbol: '₽' },
    { code: 'INR', symbol: '₹' },
    { code: 'CAD', symbol: 'C$' },
    { code: 'AUD', symbol: 'A$' },
    { code: 'BRL', symbol: 'R$' },
    { code: 'CHF', symbol: 'Fr' },
    { code: 'PLN', symbol: 'zł' },
    { code: 'TRY', symbol: '₺' },
    { code: 'ZAR', symbol: 'R' },
    { code: 'IDR', symbol: 'Rp' },
    { code: 'KRW', symbol: '₩' }
  ];

  return (
    <SettingsSection 
      title={t('currency_settings')} 
      description={t('currency_description')}
    >
      {isLoading ? (
        <p>{t('loading')}</p>
      ) : (
        <>
          <SelectContainer>
            <SelectWrapper>
              <StyledSelect 
                value={selectedCurrency} 
                onChange={handleCurrencyChange}
              >
                {currencies.map(currency => (
                  <option key={currency.code} value={currency.code}>
                    {currency.code} ({currency.symbol})
                  </option>
                ))}
              </StyledSelect>
            </SelectWrapper>
          </SelectContainer>
          <FormMessage 
            isError={message.isError} 
            visible={message.visible}
          >
            {message.text}
          </FormMessage>
        </>
      )}
    </SettingsSection>
  );
};

export default CurrencySection;